import type { DrawingEntity, EntityType } from '../contract/types.js';
import { candidateCost } from './candidate.js';
import { optimalAssignment, INFEASIBLE } from './hungarian.js';

export * from './tolerance.js';
export * from './normalise.js';
export { candidateCost, angleGap } from './candidate.js';
export { optimalAssignment, INFEASIBLE } from './hungarian.js';

export interface MatchPair {
  producedIndex: number;
  truthIndex: number;
  cost: number;
}

export interface MatchResult {
  pairs: MatchPair[];
  unmatchedProduced: number[];
  unmatchedTruth: number[];
}

/**
 * Matches produced entities against ground truth.
 *
 * The order is: decide which pairs are *candidates* at all, then choose the
 * best one-to-one assignment among them, then count. Counting before assigning
 * is how a correct extractor gets scored unfairly.
 *
 * Only entities of the same type are ever compared, so this is called once per
 * type by the scorer.
 */
export interface MatchOptions {
  /** Arc angular tolerance; see {@link candidateCost}. Defaults to the board's 0.5 degrees. */
  angleToleranceDeg?: number;
  /**
   * A further condition a pair must satisfy to be a candidate at all.
   *
   * The board's rule answers "is this the same shape", which is the right
   * question for recognition and the wrong one for a round trip: a DXF can carry
   * every coordinate perfectly and still write the text at the wrong height, on
   * the wrong layer, in the wrong colour, with a rounded corner squared off.
   * Stage 2 of the accuracy pipeline supplies that check here rather than in
   * `candidateCost`, so the board's matching rule stays exactly as specified and
   * the DXF-specific rules stay in the DXF-specific module.
   *
   * A pair that fails it is not a candidate, so the produced entity becomes a
   * false positive and the reference entity a false negative — the same
   * treatment as any other mismatch, and it reaches precision and recall
   * without a second reporting channel.
   */
  alsoRequire?: (produced: DrawingEntity, truth: DrawingEntity) => boolean;
}

export function matchEntities(
  produced: readonly DrawingEntity[],
  truth: readonly DrawingEntity[],
  tolerance: number,
  options: MatchOptions = {},
): MatchResult {
  if (produced.length === 0 || truth.length === 0) {
    return {
      pairs: [],
      unmatchedProduced: produced.map((_, i) => i),
      unmatchedTruth: truth.map((_, i) => i),
    };
  }

  const cost: number[][] = produced.map((p) =>
    truth.map((t) => {
      if (options.alsoRequire && !options.alsoRequire(p, t)) return INFEASIBLE;
      const c = candidateCost(p, t, tolerance, options.angleToleranceDeg);
      return c === null ? INFEASIBLE : c;
    }),
  );

  const assignment = optimalAssignment(cost);

  const pairs: MatchPair[] = [];
  const matchedTruth = new Set<number>();
  const unmatchedProduced: number[] = [];

  assignment.forEach((truthIndex, producedIndex) => {
    if (truthIndex >= 0) {
      pairs.push({ producedIndex, truthIndex, cost: cost[producedIndex]![truthIndex]! });
      matchedTruth.add(truthIndex);
    } else {
      unmatchedProduced.push(producedIndex);
    }
  });

  const unmatchedTruth: number[] = [];
  for (let i = 0; i < truth.length; i++) if (!matchedTruth.has(i)) unmatchedTruth.push(i);

  return { pairs, unmatchedProduced, unmatchedTruth };
}

/** Groups entities by type, so each type is matched and scored separately. */
export function groupByType(
  entities: readonly DrawingEntity[],
): Map<EntityType, DrawingEntity[]> {
  const out = new Map<EntityType, DrawingEntity[]>();
  for (const e of entities) {
    const list = out.get(e.type);
    if (list) list.push(e);
    else out.set(e.type, [e]);
  }
  return out;
}
